import { useState, useEffect, useMemo, useRef } from 'react';
import { api } from './api';
import { scopedKey } from './scope';
import './Reviews.css';

// Agent-facing reviews manager: request reviews from contacts, moderate what came
// in (approve / hide), and add testimonials by hand. Approved reviews show on the
// public profile (/agent/<slug>).

const STARS = (n) => '★★★★★'.slice(0, n) + '☆☆☆☆☆'.slice(0, 5 - n);

function contactName(c) {
  return `${c.firstName || ''} ${c.lastName || ''}`.trim() || c.email || 'Unnamed contact';
}

function StarPick({ value, onChange }) {
  const [hover, setHover] = useState(0);
  const active = hover || value;
  return (
    <span className="rvm-starpick">
      {[1, 2, 3, 4, 5].map(n => (
        <button key={n} type="button" className={`rvm-star ${n <= active ? 'on' : ''}`}
          onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)}
          onClick={() => onChange(n === value ? 0 : n)} aria-label={`${n} stars`}>★</button>
      ))}
    </span>
  );
}

function Reviews({ scope, agentName }) {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [toast, setToast] = useState('');
  const toastTimer = useRef(null);

  // Contacts (from the local cache) to solicit from.
  const contacts = useMemo(() => {
    try { return JSON.parse(localStorage.getItem(scopedKey('ira_contacts', scope)) || '[]'); }
    catch { return []; }
  }, [scope]);

  useEffect(() => {
    let alive = true;
    api.get('/api/reviews')
      .then(d => { if (alive) { setReviews(d.reviews || []); setLoading(false); } })
      .catch(e => { if (alive) { setErr(e.message || 'Could not load reviews.'); setLoading(false); } });
    return () => { alive = false; };
  }, [scope]);

  function flash(msg) {
    setToast(msg);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(''), 4000);
  }

  const patch = async (id, data) => {
    try {
      const d = await api.patch(`/api/reviews/${id}`, data);
      setReviews(rs => rs.map(r => r.id === id ? d.review : r));
    } catch (e) { flash(e.message || 'Update failed.'); }
  };
  const remove = async (id) => {
    if (!window.confirm('Delete this review permanently?')) return;
    try { await api.delete(`/api/reviews/${id}`); setReviews(rs => rs.filter(r => r.id !== id)); }
    catch (e) { flash(e.message || 'Delete failed.'); }
  };

  const pending = reviews.filter(r => r.status === 'pending');
  const approved = reviews.filter(r => r.status === 'approved');
  const requested = reviews.filter(r => r.status === 'requested');
  const hidden = reviews.filter(r => r.status === 'hidden');

  return (
    <div className="rvm">
      <div className="rvm-intro">
        <h2>Reviews</h2>
        <p>Ask happy clients for a review, then approve the ones you want shown on your public profile. Only reviews you approve are visible publicly.</p>
      </div>

      {toast && <div className="rvm-toast">{toast}</div>}
      {err && <div className="rvm-err">{err}</div>}

      <RequestCard contacts={contacts}
        onCreated={(rv) => { setReviews(rs => [rv, ...rs.filter(r => r.id !== rv.id)]); }}
        flash={flash} />

      {loading ? <div className="rvm-loading">Loading…</div> : (
        <>
          <Section title="Awaiting your approval" count={pending.length} tone="attn"
            empty="No new reviews waiting. Request some above!">
            {pending.map(r => (
              <ReviewRow key={r.id} r={r}
                actions={<>
                  <button className="rvm-btn rvm-approve" onClick={() => patch(r.id, { status: 'approved' })}>Approve</button>
                  <button className="rvm-btn rvm-ghost" onClick={() => patch(r.id, { status: 'hidden' })}>Hide</button>
                </>} />
            ))}
          </Section>

          <Section title="Published on your profile" count={approved.length}
            empty="Nothing published yet. Approve a review to show it publicly.">
            {approved.map(r => (
              <ReviewRow key={r.id} r={r}
                actions={<>
                  <button className="rvm-btn rvm-ghost" onClick={() => patch(r.id, { status: 'hidden' })}>Unpublish</button>
                  <button className="rvm-btn rvm-danger" onClick={() => remove(r.id)}>Delete</button>
                </>} />
            ))}
          </Section>

          {requested.length > 0 && (
            <Section title="Requested — awaiting a response" count={requested.length}
              empty="">
              {requested.map(r => (
                <RequestedRow key={r.id} r={r} onCopy={() => flash('Review link copied.')} onDelete={() => remove(r.id)} />
              ))}
            </Section>
          )}

          {hidden.length > 0 && (
            <Section title="Hidden" count={hidden.length} empty="">
              {hidden.map(r => (
                <ReviewRow key={r.id} r={r}
                  actions={<>
                    <button className="rvm-btn rvm-approve" onClick={() => patch(r.id, { status: 'approved' })}>Publish</button>
                    <button className="rvm-btn rvm-danger" onClick={() => remove(r.id)}>Delete</button>
                  </>} />
              ))}
            </Section>
          )}

          <AddManual onAdded={(rv) => setReviews(rs => [rv, ...rs])} flash={flash} />
        </>
      )}
    </div>
  );
}

function Section({ title, count, tone, empty, children }) {
  const items = Array.isArray(children) ? children.filter(Boolean) : (children ? [children] : []);
  return (
    <section className={`rvm-section ${tone === 'attn' && count > 0 ? 'attn' : ''}`}>
      <h3 className="rvm-section-title">{title}{count > 0 && <span className="rvm-badge">{count}</span>}</h3>
      {items.length > 0 ? <div className="rvm-list">{children}</div> : (empty ? <p className="rvm-empty">{empty}</p> : null)}
    </section>
  );
}

// Where a review came from — agents vet ONLINE (walk-in) submissions more carefully,
// since anyone on the public profile can type any email. Derivable from stored fields:
// an invited review always carries requestedAt; a walk-in has an email but no request;
// an agent-typed manual entry has neither.
function reviewSource(r) {
  if (r.requestedAt) return { label: 'Email invite', cls: 'invite' };
  if (r.authorEmail) return { label: 'Online', cls: 'online' };
  return { label: 'Added by you', cls: 'manual' };
}

function ReviewRow({ r, actions }) {
  const src = reviewSource(r);
  return (
    <div className="rvm-card">
      <div className="rvm-card-main">
        {typeof r.rating === 'number' && r.rating > 0 && <div className="rvm-cardstars">{STARS(r.rating)}</div>}
        {r.body && <div className="rvm-body">“{r.body}”</div>}
        <div className="rvm-author">
          — {r.authorName || 'Anonymous'}
          {r.authorEmail && <span className="rvm-author-email"> · {r.authorEmail}</span>}
          <span className={`rvm-source rvm-source-${src.cls}`}>{src.label}</span>
        </div>
      </div>
      <div className="rvm-actions">{actions}</div>
    </div>
  );
}

function RequestedRow({ r, onCopy, onDelete }) {
  const link = `${window.location.origin}/review/${r.token}`;
  return (
    <div className="rvm-card">
      <div className="rvm-card-main">
        <div className="rvm-author">{r.authorName || r.authorEmail || 'Contact'}</div>
        <div className="rvm-reqlink">{link}</div>
      </div>
      <div className="rvm-actions">
        <button className="rvm-btn rvm-ghost" onClick={() => { navigator.clipboard?.writeText(link); onCopy(); }}>Copy link</button>
        <button className="rvm-btn rvm-danger" onClick={onDelete}>Cancel</button>
      </div>
    </div>
  );
}

function RequestCard({ contacts, onCreated, flash }) {
  const [contactId, setContactId] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [lastLink, setLastLink] = useState('');

  const pickContact = (id) => {
    setContactId(id);
    const c = contacts.find(x => String(x.id) === String(id));
    if (c) { setName(`${c.firstName || ''} ${c.lastName || ''}`.trim()); setEmail(c.email || ''); }
  };

  async function send() {
    setSending(true); setLastLink('');
    try {
      const d = await api.post('/api/reviews/request', {
        contactId: contactId || undefined, name, email,
        appOrigin: window.location.origin,
      });
      onCreated(d.review);
      setLastLink(d.link || '');
      if (d.alreadyPending) flash('A review request is already pending for this contact — no new email sent. Copy the existing link below to re-share, or cancel it first from the list.');
      else if (d.emailed) flash(`Review request emailed to ${email}.`);
      else if (email && !d.emailConfigured) flash('Email isn’t configured — copy the link below to share it.');
      else flash('Review link ready — copy it below to share.');
      setContactId(''); setName(''); setEmail('');
    } catch (e) { flash(e.message || 'Could not create the request.'); }
    finally { setSending(false); }
  }

  return (
    <div className="rvm-request">
      <h3 className="rvm-section-title">Request a review</h3>
      <div className="rvm-req-grid">
        <label className="rvm-field">
          <span>From a contact</span>
          <select value={contactId} onChange={e => pickContact(e.target.value)}>
            <option value="">— choose a contact —</option>
            {contacts.map(c => (
              <option key={c.id} value={c.id}>{contactName(c)}{c.email ? ` (${c.email})` : ''}</option>
            ))}
          </select>
        </label>
        <label className="rvm-field">
          <span>Name</span>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="Client name" />
        </label>
        <label className="rvm-field">
          <span>Email (to send the request)</span>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="client@email.com" />
        </label>
      </div>
      <div className="rvm-req-actions">
        <button className="rvm-btn rvm-primary" onClick={send} disabled={sending || (!email && !contactId && !name)}>
          {sending ? 'Working…' : (email ? 'Send request' : 'Create link')}
        </button>
        {lastLink && (
          <div className="rvm-linkrow">
            <input className="rvm-linkinput" readOnly value={lastLink} onFocus={e => e.target.select()} />
            <button className="rvm-btn rvm-ghost" onClick={() => { navigator.clipboard?.writeText(lastLink); flash('Link copied.'); }}>Copy</button>
          </div>
        )}
      </div>
    </div>
  );
}

function AddManual({ onAdded, flash }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [rating, setRating] = useState(0);
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!name.trim() || !body.trim()) { flash('Add a name and the review text.'); return; }
    setSaving(true);
    try {
      const d = await api.post('/api/reviews', { authorName: name, rating: rating || undefined, body });
      onAdded(d.review);
      flash('Testimonial added and published.');
      setName(''); setRating(0); setBody(''); setOpen(false);
    } catch (e) { flash(e.message || 'Could not add it.'); }
    finally { setSaving(false); }
  }

  if (!open) {
    return (
      <div className="rvm-addbar">
        <button className="rvm-btn rvm-ghost" onClick={() => setOpen(true)}>+ Add a review by hand</button>
        <span className="rvm-addhint">Already have a testimonial (e.g. from Zillow or email)? Add it directly.</span>
      </div>
    );
  }
  return (
    <div className="rvm-request">
      <h3 className="rvm-section-title">Add a review by hand</h3>
      <div className="rvm-req-grid">
        <label className="rvm-field">
          <span>Reviewer name</span>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="Client name" />
        </label>
        <label className="rvm-field">
          <span>Rating (optional)</span>
          <StarPick value={rating} onChange={setRating} />
        </label>
      </div>
      <label className="rvm-field rvm-field-full">
        <span>Review</span>
        <textarea rows={3} value={body} onChange={e => setBody(e.target.value)} placeholder="What did they say?" />
      </label>
      <div className="rvm-req-actions">
        <button className="rvm-btn rvm-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Add & publish'}</button>
        <button className="rvm-btn rvm-ghost" onClick={() => setOpen(false)}>Cancel</button>
      </div>
    </div>
  );
}

export default Reviews;
